import * as React from "react";
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createGRN } from "logic/permissions/GRN";
import { ConfirmDialog, IfPermitted } from "components/common";
import UserNotification from "util/UserNotification";
import { DeleteMenuItem } from "components/bootstrap";
import StringUtils from "util/StringUtils";
import BulkActionsDropdown from "components/common/EntityDataTable/BulkActionsDropdown";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { COLLECTION_ENTITIES_QUERY_KEY } from "collections/hooks/useCollectionEntities";
import useCollectionEntityMutation from "collections/hooks/useCollectionEntityMutation";
const getDescriptor = (count) => StringUtils.pluralize(count, "item", "items");
const BulkActions = ({ collectionId, selectedEntities }) => {
  const { bulkRemoveEntitiesMutation } = useCollectionEntityMutation(collectionId);
  const queryClient = useQueryClient();
  const { setSelectedEntities } = useSelectedEntities();
  const [showDialog, setShowDialog] = useState(false);
  const selectedItemsAmount = selectedEntities == null ? void 0 : selectedEntities.length;
  const refetchCollection = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY, collectionId]
    });
    queryClient.invalidateQueries({
      queryKey: [COLLECTION_ENTITIES_QUERY_KEY, collectionId]
    });
  }, [queryClient, collectionId]);
  const handleDelete = () => {
    setShowDialog(true);
  };
  const handleClearState = () => {
    setShowDialog(false);
    refetchCollection();
  };
  const onDelete = useCallback(() => {
    bulkRemoveEntitiesMutation.mutateAsync({ removedEntities: selectedEntities.map((entity) => createGRN(entity.type, entity.id)) }).then(({ failures }) => {
      if (failures == null ? void 0 : failures.length) {
        const notUpdatedCollectionEntityIds = failures.map(({ entity_id }) => entity_id);
        setSelectedEntities(notUpdatedCollectionEntityIds);
        UserNotification.error(
          `${notUpdatedCollectionEntityIds.length} out of ${selectedItemsAmount} selected ${getDescriptor(selectedItemsAmount)} could not be removed.`
        );
      } else {
        setSelectedEntities([]);
        UserNotification.success(
          `${selectedItemsAmount} ${getDescriptor(selectedItemsAmount)} ${StringUtils.pluralize(selectedItemsAmount, "was", "were")} removed successfully.`,
          "Success"
        );
      }
    }).catch((error) => {
      UserNotification.error(`An error occurred while removing content. ${error}`);
    }).finally(() => {
      refetchCollection();
    });
  }, [bulkRemoveEntitiesMutation, refetchCollection, selectedEntities, selectedItemsAmount, setSelectedEntities]);
  const handleConfirm = () => {
    onDelete();
    setShowDialog(false);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BulkActionsDropdown, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `collection_entities:edit:${collectionId}` }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: handleDelete }, "Remove"))), showDialog && /* @__PURE__ */ React.createElement(ConfirmDialog, { title: "Remove Entities", show: true, onConfirm: handleConfirm, onCancel: handleClearState }, "Are you sure you want to remove ", selectedItemsAmount, " ", getDescriptor(selectedItemsAmount), "?"));
};
export default BulkActions;
